import React from 'react';
import { Task, User } from '../types';
import { TrendingUp, Calendar, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  WIDGET_ALERT_DANGER_CLASS,
  WIDGET_ALERT_DANGER_TEXT_CLASS,
  WIDGET_ALERT_INFO_CLASS,
  WIDGET_ALERT_INFO_TEXT_CLASS,
  WIDGET_ALERT_SUCCESS_CLASS,
  WIDGET_ALERT_SUCCESS_TEXT_CLASS,
  WIDGET_ALERT_WARNING_CLASS,
  WIDGET_ALERT_WARNING_TEXT_CLASS,
  WIDGET_MUTED_TEXT_CLASS,
  WIDGET_PANEL_CLASS,
  WIDGET_SUBTLE_PANEL_CLASS,
} from '../constants/widgetTheme';

interface CompletionTimeProjectionProps {
  task: Task;
  allTasks: Task[];
  users: User[];
}

export default function CompletionTimeProjection({
  task,
  allTasks,
  users,
}: CompletionTimeProjectionProps) {
  // Calculate task metrics
  const estimatedHours = task.estimatedHours || 0;
  const totalLogged = task.timeLogs?.reduce((sum, log) => sum + log.hours, 0) || 0;
  const remainingHours = Math.max(0, estimatedHours - totalLogged);

  // Get subtasks
  const subtasks = task.subtaskIds
    .map((id) => allTasks.find((t) => t.id === id))
    .filter(Boolean) as Task[];

  // Calculate subtask metrics
  const subtaskEstimated = subtasks.reduce((sum, st) => sum + (st.estimatedHours || 0), 0);
  const subtaskLogged = subtasks.reduce(
    (sum, st) => sum + (st.timeLogs?.reduce((s, log) => s + log.hours, 0) || 0),
    0
  );
  const subtaskRemaining = Math.max(0, subtaskEstimated - subtaskLogged);

  // Total remaining work
  const totalRemaining = remainingHours + subtaskRemaining;

  // Get assignees
  const assignees = users.filter((u) => task.assigneeIds.includes(u.id));

  // Calculate average hours per day (assuming work has been logged)
  const workingDays = getWorkingDaysBetween(getEarliestLogDate(task), new Date());
  const avgHoursPerDay = workingDays > 0 ? totalLogged / workingDays : 4; // Default to 4h/day

  // Use velocity if available, otherwise use standard 6h/day per person
  const velocityHoursPerDay = avgHoursPerDay > 0 ? avgHoursPerDay : 6 * assignees.length;

  // Project completion
  const daysToComplete = velocityHoursPerDay > 0 ? totalRemaining / velocityHoursPerDay : 0;
  const projectedCompletionDate = addWorkingDays(new Date(), Math.ceil(daysToComplete));

  // Check dependencies
  const dependencies = task.dependencies?.split(',').filter(Boolean) || [];
  const dependencyTasks = dependencies
    .map((id) => allTasks.find((t) => t.id === id.trim()))
    .filter(Boolean) as Task[];
  const incompleteDeps = dependencyTasks.filter(
    (dep) => dep.status !== 'Done' && dep.status !== 'Completed'
  );

  // Get latest dependency completion date
  let earliestStartDate = new Date();
  if (incompleteDeps.length > 0) {
    const latestDepDate = getLatestProjectedDate(incompleteDeps, allTasks, users);
    if (latestDepDate > earliestStartDate) {
      earliestStartDate = latestDepDate;
    }
  }

  // Adjust projected date if dependencies delay start
  const finalProjectedDate =
    earliestStartDate > new Date()
      ? addWorkingDays(earliestStartDate, Math.ceil(daysToComplete))
      : projectedCompletionDate;

  // Compare with target date
  const targetDate = task.targetCompletionDate ? new Date(task.targetCompletionDate) : null;
  const isLate = targetDate && finalProjectedDate > targetDate;
  const daysVariance = targetDate
    ? Math.ceil((finalProjectedDate.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // Confidence level
  const confidence = calculateConfidence(task, totalLogged, avgHoursPerDay);

  return (
    <Card className={WIDGET_PANEL_CLASS}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Completion Projection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Projected completion date */}
        <div className={`p-4 ${isLate ? WIDGET_ALERT_DANGER_CLASS : WIDGET_ALERT_SUCCESS_CLASS}`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span className="font-semibold text-card-foreground">
                Projected Completion
              </span>
            </div>
            <Badge variant={isLate ? 'destructive' : 'default'} className="text-xs">
              {confidence}% confident
            </Badge>
          </div>
          <div className="mb-1 text-2xl font-bold text-card-foreground">
            {finalProjectedDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </div>
          {targetDate && (
            <div className="flex items-center gap-2 text-sm">
              <span className={WIDGET_MUTED_TEXT_CLASS}>Target:</span>
              <span className="font-medium">
                {targetDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              {isLate ? (
                <Badge variant="destructive" className="text-xs">
                  {daysVariance} days late
                </Badge>
              ) : daysVariance < 0 ? (
                <Badge className="text-xs bg-chart-2/10">
                  {Math.abs(daysVariance)} days early
                </Badge>
              ) : (
                <Badge className="text-xs bg-chart-2/10">On time</Badge>
              )}
            </div>
          )}
        </div>

        {/* Work breakdown */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-card-foreground">
            Work Breakdown
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className={`${WIDGET_SUBTLE_PANEL_CLASS} p-3`}>
              <div className={`mb-1 text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>Main Task</div>
              <div className="text-xl font-bold text-card-foreground">
                {remainingHours.toFixed(1)}h
              </div>
              <div className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>remaining</div>
            </div>
            {subtasks.length > 0 && (
              <div className={`${WIDGET_SUBTLE_PANEL_CLASS} p-3`}>
                <div className={`mb-1 text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>
                  {subtasks.length} Subtasks
                </div>
                <div className="text-xl font-bold text-card-foreground">
                  {subtaskRemaining.toFixed(1)}h
                </div>
                <div className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>remaining</div>
              </div>
            )}
          </div>
          <div className={`${WIDGET_ALERT_INFO_CLASS} p-3`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-card-foreground">
                Total Remaining
              </span>
              <span className={`text-2xl font-bold ${WIDGET_ALERT_INFO_TEXT_CLASS}`}>
                {totalRemaining.toFixed(1)}h
              </span>
            </div>
          </div>
        </div>

        {/* Velocity */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-card-foreground">Velocity</h4>
          <div className="flex items-center gap-2 text-sm">
            <Clock className={`h-4 w-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
            <span className={WIDGET_MUTED_TEXT_CLASS}>
              {velocityHoursPerDay.toFixed(1)} hours/day
            </span>
            {assignees.length > 0 && (
              <span className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>({assignees.length} assignees)</span>
            )}
          </div>
          <div className={`text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>
            Based on {workingDays > 0 ? 'actual progress' : 'estimated capacity'}
          </div>
        </div>

        {/* Warnings */}
        {incompleteDeps.length > 0 && (
          <div className={`${WIDGET_ALERT_WARNING_CLASS} flex items-start gap-2 p-3`}>
            <AlertTriangle className={`mt-0.5 h-4 w-4 flex-shrink-0 ${WIDGET_ALERT_WARNING_TEXT_CLASS}`} />
            <div className="text-sm">
              <div className={`mb-1 font-medium ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>
                Blocked by {incompleteDeps.length} dependencies
              </div>
              <div className={`text-xs ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>
                Work cannot start until dependencies are completed
              </div>
            </div>
          </div>
        )}

        {confidence < 50 && (
          <div className={`${WIDGET_ALERT_DANGER_CLASS} flex items-start gap-2 p-3`}>
            <AlertTriangle className={`mt-0.5 h-4 w-4 flex-shrink-0 ${WIDGET_ALERT_DANGER_TEXT_CLASS}`} />
            <div className="text-sm">
              <div className={`mb-1 font-medium ${WIDGET_ALERT_DANGER_TEXT_CLASS}`}>
                Low confidence projection
              </div>
              <div className={`text-xs ${WIDGET_ALERT_DANGER_TEXT_CLASS}`}>
                Limited data available. Log more hours for accurate projections.
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Helper functions
function getEarliestLogDate(task: Task): Date {
  if (!task.timeLogs || task.timeLogs.length === 0) return new Date();
  const dates = task.timeLogs.map((log) => new Date(log.date));
  return new Date(Math.min(...dates.map((d) => d.getTime())));
}

function getWorkingDaysBetween(startDate: Date, endDate: Date): number {
  let days = 0;
  const current = new Date(startDate);
  while (current <= endDate) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) days++; // Skip weekends
    current.setDate(current.getDate() + 1);
  }
  return Math.max(days, 1);
}

function addWorkingDays(startDate: Date, daysToAdd: number): Date {
  const result = new Date(startDate);
  let added = 0;
  while (added < daysToAdd) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) added++; // Skip weekends
  }
  return result;
}

function getLatestProjectedDate(tasks: Task[], allTasks: Task[], users: User[]): Date {
  // Simplified: use target dates or add 7 days for incomplete dependencies
  const dates = tasks.map((t) => {
    if (t.targetCompletionDate) return new Date(t.targetCompletionDate);
    return addWorkingDays(new Date(), 7);
  });
  return new Date(Math.max(...dates.map((d) => d.getTime())));
}

function calculateConfidence(task: Task, totalLogged: number, avgHoursPerDay: number): number {
  // Higher confidence with more logged hours and consistent velocity
  const estimatedHours = task.estimatedHours || 0;
  if (estimatedHours === 0) return 30;

  const progressPercent = totalLogged / estimatedHours;
  const hasVelocity = avgHoursPerDay > 0 ? 20 : 0;
  const hasLogs = task.timeLogs && task.timeLogs.length > 0 ? 20 : 0;
  const progressBonus = Math.min(progressPercent * 60, 60);

  return Math.min(Math.round(hasVelocity + hasLogs + progressBonus), 95);
}
